import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'

const baseUrl = '/secure/policies/purview/retention-compliance-policy'

const PENDING_DELETION = 'PendingDeletion'

/**
 * Retention policies pending removal should not appear in the live list.
 * Microsoft may surface this via Mode and/or DistributionStatus.
 */
const isPendingDeletion = (policy) =>
  policy?.Mode === PENDING_DELETION || policy?.DistributionStatus === PENDING_DELETION

export const purviewRetentionApi = createApi({
  reducerPath: 'purviewRetention',
  tagTypes: ['RetentionPolicy', 'RetentionPolicies'],
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getRetentionPolicies: builder.query({
      query: (customerId) => ({
        url: baseUrl,
        params: { customerId },
      }),
      transformResponse: (response) => (response ?? []).filter((policy) => !isPendingDeletion(policy)),
      providesTags: (result, error, customerId) => [{ type: 'RetentionPolicies', id: customerId }],
    }),
    getRetentionPolicyById: builder.query({
      query: ({ customerId, policyId }) => ({
        url: `${baseUrl}/${policyId}`,
        params: { customerId },
      }),
      transformResponse: (response) => {
        const { rawDataFlat, ...rest } = response ?? {}
        return { ...rawDataFlat, ...rest }
      },
      providesTags: (result, error, { policyId }) => [{ type: 'RetentionPolicy', id: policyId }],
    }),
    createRetentionPolicy: builder.mutation({
      query: ({ customerId, hubTaskSource, payload }) => ({
        url: baseUrl,
        body: payload,
        params: { customerId, source: hubTaskSource },
        method: 'POST',
      }),
      invalidatesTags: (result, error, { customerId }) => [{ type: 'RetentionPolicies', id: customerId }],
    }),
    deleteRetentionPolicy: builder.mutation({
      query: ({ customerId, policyId }) => ({
        url: `${baseUrl}/${policyId}`,
        method: 'DELETE',
        params: { customerId },
      }),
      invalidatesTags: (result, error, { customerId }) => [{ type: 'RetentionPolicies', id: customerId }],
    }),
  }),
})

export const {
  useGetRetentionPoliciesQuery,
  useLazyGetRetentionPoliciesQuery,
  useGetRetentionPolicyByIdQuery,
  useLazyGetRetentionPolicyByIdQuery,
  useCreateRetentionPolicyMutation,
  useDeleteRetentionPolicyMutation,
} = purviewRetentionApi

export const useGetRetentionPolicies = (options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetRetentionPoliciesQuery(customerId, { ...options, skip: !customerId || options.skip })
}

export const useGetRetentionPolicyById = ({ policyId }, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetRetentionPolicyByIdQuery(
    { customerId, policyId },
    { ...options, skip: !customerId || !policyId || options.skip },
  )
}
